import {
  applySnapshot,
  flow,
  getSnapshot,
  SnapshotOrInstance,
  types,
} from "mobx-state-tree";
import { addDays, eachDayOfInterval, startOfWeek } from "date-fns";

import FoodActiveProgramDictionary from "stores/dictionary/FoodActiveProgramDictionary";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { reorder } from "utils/calculation";
import { ignoreTimezone } from "utils/date";
import { getNextPublishStatus } from "utils/publish";

import {
  ActiveProgram,
  FOOD_PROGRAM_TYPES,
  NutritionCategoryModel,
  PROGRAM_PERMISSION_TYPES,
  PUBLISH_STATUSES,
  SimpleModel,
  SimpleModelType,
  STRUCTURE_TYPES,
} from "constants/models";
import { REORDER_MOVE_TYPES } from "constants/reorder";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";

export const FoodCategoryDay = types.model({
  dayOfWeek: types.number,
  foodDayType: SimpleModel,
});

export const CategoryDetailsModel = types
  .model({
    id: types.number,
    title: types.string,
    subTitle: types.maybe(types.string),
    note: types.maybe(types.string),
    imageUrl: types.maybe(types.string),
    activePrograms: types.array(ActiveProgram),
    startDate: types.maybe(types.string),
    structureType: types.enumeration(Object.values(STRUCTURE_TYPES)),
    foodProgramType: types.enumeration(Object.values(FOOD_PROGRAM_TYPES)),
    weekNumber: types.maybe(types.number),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    foodCategoryDays: types.array(FoodCategoryDay),
  })
  .views(self => ({
    get isWeekCategory() {
      return self.structureType === STRUCTURE_TYPES.WEEK;
    },
  }))
  .views(self => ({
    get isDeprecatedCategoryType() {
      return (
        self.isWeekCategory &&
        self.startDate &&
        self.foodProgramType === FOOD_PROGRAM_TYPES.UNKNOWN
      );
    },

    get isSixWeekCategory() {
      return (
        self.isWeekCategory &&
        self.foodProgramType === FOOD_PROGRAM_TYPES.SIX_WEEK
      );
    },

    get weekText() {
      return `Week ${self.weekNumber}`;
    },
  }))
  .views(self => ({
    get shouldPrepopulateAccessProgramsDeprecatedCategory() {
      const vipAndOtherProgramsIds = FoodActiveProgramDictionary.getOptionsByFoodProgram(
        FOOD_PROGRAM_TYPES.VIP_AND_OTHER,
      )?.map(({ value }) => value.id);
      const activeProgramsIds = self.activePrograms.map(({ id }) => id);

      const shouldPrepopulateActivePrograms = !activeProgramsIds?.find(
        id => !vipAndOtherProgramsIds?.includes(id),
      );

      return self.isDeprecatedCategoryType && shouldPrepopulateActivePrograms;
    },
  }))
  .views(self => ({
    get formData() {
      return {
        ...self,
        file: self.imageUrl,
        foodProgramType:
          self.foodProgramType !== FOOD_PROGRAM_TYPES.UNKNOWN
            ? self.foodProgramType
            : self.shouldPrepopulateAccessProgramsDeprecatedCategory
            ? FOOD_PROGRAM_TYPES.VIP_AND_OTHER
            : undefined,
        activePrograms:
          self.shouldPrepopulateAccessProgramsDeprecatedCategory ||
          !self.isDeprecatedCategoryType
            ? self.activePrograms.map(({ id, permission }) => ({
                id,
                switchState: permission === PROGRAM_PERMISSION_TYPES.PREVIEW,
              }))
            : [],
        startDate: self.startDate
          ? new Date(ignoreTimezone(self.startDate))
          : undefined,
      };
    },
  }))
  .views(self => ({
    get weekCategoryDays() {
      if (self.isWeekCategory) {
        const startDate = !self.isSixWeekCategory
          ? self.startDate
            ? new Date(ignoreTimezone(self.startDate))
            : undefined
          : startOfWeek(new Date(), { weekStartsOn: 1 });

        return startDate
          ? eachDayOfInterval({
              start: startDate,
              end: addDays(startDate, 6),
            })
          : [];
      }
    },
  }));

const InAppNutritionStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/food/category/list`, params);
  }),
  itemModel: NutritionCategoryModel,
})
  .props({
    categoryDetails: types.maybe(CategoryDetailsModel),
    isCategoryLoading: types.optional(types.boolean, false),
    isCategoryManaging: types.optional(types.boolean, false),
    isCategoryReordering: types.optional(types.boolean, false),
    isCategoryPublishing: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
    isCategoryCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    getReorderInfo(
      reorderedItems: SnapshotOrInstance<typeof NutritionCategoryModel>[],
      categoryId: number,
    ) {
      const categoryIdNewIndex = reorderedItems.findIndex(
        (item: SnapshotOrInstance<typeof NutritionCategoryModel>) =>
          item.id === categoryId,
      );

      switch (categoryIdNewIndex) {
        case 0:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.TOP_OF_PAGE,
          };
        case self.items.length - 1:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.BOTTOM_OF_PAGE,
          };
        default:
          return {
            afterId: reorderedItems[categoryIdNewIndex - 1].id,
            moveType: REORDER_MOVE_TYPES.NORMAL,
          };
      }
    },

    applyNewFoodCategoryDays(dayOfWeek: number, foodDayType: SimpleModelType) {
      if (self.categoryDetails) {
        const existingDays =
          getSnapshot(self).categoryDetails?.foodCategoryDays ?? [];

        const updatedDays = [
          ...existingDays.filter(
            existingDay => existingDay.dayOfWeek !== dayOfWeek,
          ),
          {
            dayOfWeek,
            foodDayType,
          },
        ];

        applySnapshot(self, {
          ...self,
          categoryDetails: {
            ...self.categoryDetails,
            foodCategoryDays: updatedDays,
          },
        });
      }
    },

    updateCategory(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...self.items[index], ...data };
      }
    },
  }))

  .actions(self => ({
    fetchMoreCategories: flow(function*() {
      self.isCategoryLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/food/category/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
            ...self.filters,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryLoading = false;
      }
    }),

    fetchCategoryDetails: flow(function*(id: number) {
      self.isCategoryLoading = true;

      try {
        const categoryDetails = yield api.get(`/food/category/${id}`);

        applySnapshot(self, {
          ...self,
          categoryDetails,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryLoading = false;
      }
    }),

    createCategory: flow(function*(payload: any) {
      self.isCategoryManaging = true;

      try {
        return yield api.postFormData(`/food/category`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    editCategoryInfo: flow(function*(payload: any) {
      self.isCategoryManaging = true;

      try {
        return yield api.put(`/food/category/${payload.id}`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    editCategoryImage: flow(function*(payload: any) {
      self.isCategoryManaging = true;

      try {
        return yield api.putFormData(
          `/food/category/${payload.id}/photo/upload`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    reorderCategories: flow(function*({
      startIndex,
      endIndex,
    }: {
      startIndex: number;
      endIndex: number;
    }) {
      self.isCategoryReordering = true;
      const { items } = getSnapshot(self);

      try {
        const categoryId = items[startIndex].id;
        const reorderedItems = reorder(items, startIndex, endIndex);
        applySnapshot(self, { ...self, items: reorderedItems });

        const reorderInfo = self.getReorderInfo(reorderedItems, categoryId);

        yield api.post(`/food/category/move`, {
          id: categoryId,
          ...reorderInfo,
        });
      } catch (error) {
        console.log("error :>> ", error);
        applySnapshot(self, { ...self, items });
        throw error;
      } finally {
        self.isCategoryReordering = false;
      }
    }),

    archiveCategory: flow(function*(id: number) {
      self.isCategoryManaging = true;

      try {
        return yield api.post(`/food/category/${id}/archive`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    updateDayType: flow(function*(payload: any) {
      self.isCategoryManaging = true;

      try {
        const { categoryId, recipeId, ...data } = payload;

        const { dayOfWeek, foodDayType } = yield api.put(
          `/food/category/${categoryId}/day-type`,
          data,
        );
        if (dayOfWeek && foodDayType) {
          self.applyNewFoodCategoryDays(dayOfWeek, foodDayType);
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    updatePublishStatus: flow(function*(id: number, status: string) {
      self.isCategoryPublishing = true;

      try {
        const newStatus = getNextPublishStatus(status);
        if (newStatus) {
          const { success } = yield api.post(`/food/category/${id}/status`, {
            status: newStatus,
          });
          if (success) {
            self.updateCategory(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryPublishing = false;
      }
    }),

    copyCategory: flow(function*(id: number) {
      self.isCategoryCopying = true;

      try {
        yield api.post(`/food/category/${id}/copy`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryCopying = false;
      }
    }),

    resetCategoryDetails() {
      applySnapshot(self, { ...self, categoryDetails: undefined });
    },
  }));

export const InAppNutritionArchivedStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/food/category/archived/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: NutritionCategoryModel,
  searchBy: "search",
})
  .props({
    isManaging: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get options() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, imageUrl, publishStatus }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/food/category/archived/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    restore: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        return yield api.post(`/food/category/restore`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),
  }))
  .create();

export default InAppNutritionStore.create();
